import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  useMediaQuery,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { useStore } from '@stores';
import { globalTheme as theme } from '@styles';

import { useStyles } from './ApprovedProviders.styles';
import { getCreditCounselingProviderItems } from './approvedProviders.utils';

export const ApprovedProviders = observer(() => {
  const { t } = useTranslation('approvedProviders');
  const {
    commonStore,
    initializeStore,
    partnerStore: { partner, whiteLabelEnabled },
  } = useStore();
  const classes = useStyles();

  const toggleDrawer = (open: boolean) => () => {
    if (open) {
      commonStore.setOpenProviders(open);
    } else {
      commonStore.setOpenProviders(open);
    }
  };

  const sectionOneItems = getCreditCounselingProviderItems({
    items: (t('sectionOne.items', { returnObjects: true }) as string[]) || [],
    variant: initializeStore.getVariant(),
    partnerDisplayName: partner?.displayname,
    whiteLabelEnabled,
  });
  const sectionTwoItems: string[] =
    (t('sectionTwo.items', { returnObjects: true }) as string[]) || [];
  const sectionThreeItems: string[] =
    (t('sectionThree.items', { returnObjects: true }) as string[]) || [];
  const sectionFourItems: string[] =
    (t('sectionFour.items', { returnObjects: true }) as string[]) || [];
  const sectionSevenItems: string[] =
    (t('sectionSeven.items', { returnObjects: true }) as string[]) || [];

  const isMobile = useMediaQuery('(max-width:960px)');
  return (
    <Drawer
      open={commonStore.openProviders}
      anchor={isMobile ? 'bottom' : 'right'}
      onClose={toggleDrawer(false)}
      sx={{
        zIndex: 2000,
        width: isMobile ? '100%' : '500px',
        height: isMobile ? 'calc(100% - 150px)' : '100%',
        maxWidth: '500px',
        '& .MuiDrawer-paper': {
          width: isMobile ? '100%' : '500px',
          height: isMobile ? 'calc(100% - 150px)' : '100%',
          borderTopLeftRadius: isMobile ? '20px' : '0',
          borderTopRightRadius: isMobile ? '20px' : '0',
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection="row"
        alignItems="center"
        px={3}
        sx={{
          borderBottom: 'solid 1px #e8e8e8',
          height: '10%',
        }}
      >
        <Typography
          fontSize={'18px'}
          variant={'body1'}
          lineHeight={1.5}
          fontWeight={600}
          color={theme.palette.text.primary}
        >
          {t('title')}
        </Typography>
        <IconButton
          onClick={toggleDrawer(false)}
          sx={{
            color: isMobile ? '#fff' : 'currentcolor',
            width: '2rem',
            height: '2rem',
            backgroundColor: isMobile ? '#2f90af' : 'transparent',
            margin: '0.5rem',
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Box
        px={3}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
        }}
        id="Approved_Providers_Drawer"
      >
        <Suspense fallback={<div>Loading...</div>}>
          <Typography className={classes.subHeader}>
            {t('sectionOne.title')}
          </Typography>
          {sectionOneItems.map((item, index) => (
            <Typography
              key={`section-one-${index}`}
              className={classes.listItem}
            >
              {index + 1}. {item}
            </Typography>
          ))}

          <Typography className={classes.subHeader}>
            {t('sectionTwo.title')}
          </Typography>
          {sectionTwoItems.map((item, index) => (
            <Typography
              key={`section-two-${index}`}
              className={classes.listItem}
            >
              {index + 1}. {item}
            </Typography>
          ))}

          <Typography className={classes.subHeader}>
            {t('sectionThree.title')}
          </Typography>
          {sectionThreeItems.map((item, index) => (
            <Typography
              key={`section-three-${index}`}
              className={classes.listItem}
            >
              {index + 1}. {item}
            </Typography>
          ))}

          <Typography className={classes.subHeader}>
            {t('sectionFour.title')}
          </Typography>
          {sectionFourItems.map((item, index) => (
            <Typography
              key={`section-four-${index}`}
              className={classes.listItem}
            >
              {index + 1}. {item}
            </Typography>
          ))}

          <Typography className={classes.subHeader}>
            {t('sectionFive.title')}
          </Typography>
          <Typography className={classes.listItem}>
            {1}. {t('sectionFive.items')}
          </Typography>

          <Typography className={classes.listItem}>
            {1}. {t('sectionSix.items')}
          </Typography>

          <Typography className={classes.subHeader}>
            {t('sectionSeven.title')}
          </Typography>
          {sectionSevenItems.map((item, index) => (
            <Typography
              key={`section-seven-${index}`}
              className={classes.listItem}
            >
              {' '}
              {index + 1}. {item}
            </Typography>
          ))}
        </Suspense>
      </Box>
      <Box
        px={3}
        sx={{
          width: '100%',
          my: 2,
          borderTop: 'solid 1px #e8e8e8',
        }}
      >
        <Button
          onClick={toggleDrawer(false)}
          fullWidth
          sx={{
            width: '100%',
            lineHeight: 1.2,
            letterSpacing: '0.25px',
            minHeight: '60px',
            minWidth: '100%',
            mt: '26px',
          }}
          variant="contained"
        >
          {t('buttonLabel')}
        </Button>
      </Box>
    </Drawer>
  );
});
